import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CommonForm from "./CommonForm";
import PatternKeisouForm from "./PatternKeisouForm";
import PatternSystemForm from "./PatternSystemForm";
import PatternKeisokuForm from "./PatternKeisokuForm";
import "./DocumentRegister.css";
import Modal from "../Modal/Modal";
import Breadcrumb from "../Breadcrumb";
import { fetchMenuData } from "../../actions/sidemenu";
import { fetchUserData } from "../../actions/user";

import BASE_URL from "../../constants/Api";
import axiosInstance from "../../helpers/refreshTokenMiddleware"; // API使用時にコメントアウト解除
import { registerDocument, clearFormData } from "../../actions/DocumentActions";

// Mock API Flag
const USE_MOCK_API = false;

const DocumentRegisterBase = ({ user }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showUserName, setUserName] = useState("");

  //初期表示・ユーザー情報設定
  useEffect(() => {
    if (user && user.user_name) {
      setUserName(user.user_name);
    }
  }, [user]);
  const token = localStorage.getItem("accessToken");
  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      try {
        await dispatch(fetchMenuData());
      } catch (error) {
        console.error("Error fetching menu data:", error);
      }
    };

    fetchData();
  }, [token, dispatch]);

  useEffect(() => {
    if (token) {
      dispatch(fetchUserData());
    }
  }, [dispatch, token]);

  // Redux state
  const { error: reduxError } = useSelector((state) => state.document);

  // ユーザー情報をReduxから取得 (state.login.user )
  const currentUserName = user?.name || user?.username || user?.user_name || "";

  // ReduxとURLがある場合
  const { deptId, folderId } = useParams(); //URLから
  const menuData = useSelector((state) => state.menu.menuData); //reduxからメニュー情報取得

  const dept = menuData.find((d) => d.department_id === deptId);
  const folder = dept?.folder?.find((f) => f.folder_id === folderId) || null;

  const departmentNameFromRedux = dept?.department_name || "";
  const folderNameFromRedux = folder?.folder_name || "";

  const attributePatternText = departmentNameFromRedux
    ? `${departmentNameFromRedux}テンプレート`
    : "";

  const inferPatternFromFolderName = (name = "") => {
    if (!name) return "DEFAULT";

    // 計装・システム制御
    if (name.includes("E版")) return "E";
    if (name.includes("T版")) return "T";
    if (name.includes("D版")) return "D";
    if (name.includes("C版")) return "C";
    if (name.includes("B版")) return "B";

    // システム装置
    if (name.includes("EM版")) return "EM";
    if (name.includes("TM版")) return "TM";
    if (name.includes("DM版")) return "DM";
    if (name.includes("CM版")) return "CM";
    if (name.includes("BM版")) return "BM";
    if (name.includes("図面表")) return "ZM";
    if (name.includes("購入品リスト")) return "KM";

    // 計測
    if (name.includes("EK版")) return "EK";
    if (name.includes("TK版")) return "TK";
    if (name.includes("DK版")) return "DK";
    if (name.includes("CK版")) return "CK";

    return "DEFAULT";
  };
  const inferredPattern = inferPatternFromFolderName(folderNameFromRedux);

  // CommonFormからのパターン変更処理
  const handlePatternChange = (pattern) => {
    // フォルダ名から推論される
    console.log("Pattern change:", pattern);
  };

  // 属性パターンテキスト取得
  const getAttributePatternText = () => {
    return attributePatternText;
  };

  // フォーム状態
  const [autoNumber, setAutoNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [patternMaster, setPatternMaster] = useState({
    document_category: [],
    document_type: [],
    assignee: "",
    issue_datetime: "",
  });
  const [patternMasterLoading, setPatternMasterLoading] = useState(false);
  const [isHakkouDisabled, setIsHakkouDisabled] = useState(false);

  //メッセージ表示
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formResetToken, setFormResetToken] = useState(0);

  // 確認モーダル（戻るボタン用）
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  // 発行機能のAPI呼び出し（オートナンバー生成）
  const handleIssue = async () => {
    setLoading(true);

    try {
      if (USE_MOCK_API) {
        // ====== MOCK API（現在使用中） ======
        // Mock API呼び出し - API呼び出しをシミュレート
        await new Promise((resolve) => setTimeout(resolve, 1000)); // 1秒遅延

        // Mockデータ - APIレスポンスをシミュレート
        const mockAutoNumber = generateMockAutoNumber(inferredPattern);
        setAutoNumber(mockAutoNumber);

        //発行済後にボタン無効化
        setIsHakkouDisabled(true);

        console.log("発行成功（Mock）:", mockAutoNumber);
      } else {
        // ====== 実際のAPI ======
        // API: /top/auto-number
        // Request body: { folder_id: "F016", department_id: "D003" }
        // Response: { status: 200, respons: { auto_number: "100003" } }
        const response = await axiosInstance.post(
          `${BASE_URL}/top/auto-number`,
          {
            folder_id: folderId,
            department_id: deptId,
          }
        );

        // レスポンスからオートナンバー取得
        // Response structure: { status: 200, respons: { auto_number: "100003" } }
        const autoNumberFromAPI = response.data.respons?.auto_number;
        if (!autoNumberFromAPI)
          throw new Error("オートナンバーが取得できませんでした");

        // Vd: auto_number = "100003", inferredPattern = "CK" → "CK-100003"
        const prefix =
          inferredPattern && inferredPattern !== "DEFAULT"
            ? inferredPattern
            : "XX";
        const formattedAutoNumber = `${prefix}-${autoNumberFromAPI}`;

        setAutoNumber(formattedAutoNumber);

        //発行済後にボタン無効化
        setIsHakkouDisabled(true);

        console.log("発行成功（実際のAPI）:", formattedAutoNumber);
      }
    } catch (error) {
      // Response format: {status: 500, respons: {message: "..."}} とか {status: 500, message: "..."}
      const errorMessage =
        error.response?.data?.respons?.message ||
        error.response?.data?.message ||
        (error.message
          ? "発番エラーが発生しました: " + error.message
          : "発番エラーが発生しました");

      setModalMessage(errorMessage);
      setIsModalOpen(true);
      console.error("発行エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  // オートナンバー作成（実際のAPI呼び出しに置き換え予定）
  const generateMockAutoNumber = (pattern) => {
    const randomNumber = Math.floor(Math.random() * 10000) + 1;
    const paddedNumber = randomNumber.toString().padStart(5, "0");

    // 不正なフォルダーの場合、XXを使用
    const prefix = pattern && pattern !== "DEFAULT" ? pattern : "XX";

    return `${prefix}-${paddedNumber}`;
  };

  // フォーム状態
  // パターンフォームのデータ状態取得
  const [patternFormData, setPatternFormData] = useState({});
  const [commonFormData, setCommonFormData] = useState({});

  const sortByDisplayIdx = (list = []) =>
    [...list].sort((a, b) => (a?.display_idx || 0) - (b?.display_idx || 0));

  const getMockPatternMaster = (deptId, foldId) => {
    if (deptId === "D001") {
      return {
        status: 200,
        respons: {
          document_category: [
            {
              department_id: "D001",
              document_category: 9,
              document_category_text: "構成図／ブロック図／系統図",
              display_idx: 1,
            },
            {
              department_id: "D001",
              document_category: 1,
              document_category_text: "仕様書（見積／納入／決定図）",
              display_idx: 2,
            },
            {
              department_id: "D001",
              document_category: 10,
              document_category_text: "ソフト設計書類",
              display_idx: 3,
            },
            {
              department_id: "D001",
              document_category: 7,
              document_category_text: "フローシート／Ｐ＆ＩＤ",
              display_idx: 4,
            },
            {
              department_id: "D001",
              document_category: 6,
              document_category_text: "外形寸法図",
              display_idx: 5,
            },
            {
              department_id: "D001",
              document_category: 12,
              document_category_text: "内部配置図／部品配置図",
              display_idx: 6,
            },
            {
              department_id: "D001",
              document_category: 4,
              document_category_text: "端子番号表",
              display_idx: 7,
            },
            {
              department_id: "D001",
              document_category: 2,
              document_category_text: "結線図",
              display_idx: 8,
            },
            {
              department_id: "D001",
              document_category: 5,
              document_category_text: "ＰＬＣ結線図",
              display_idx: 9,
            },
            {
              department_id: "D001",
              document_category: 13,
              document_category_text: "回路図",
              display_idx: 10,
            },
            {
              department_id: "D001",
              document_category: 11,
              document_category_text: "購入書類（製作仕様書／手配）",
              display_idx: 11,
            },
            {
              department_id: "D001",
              document_category: 15,
              document_category_text: "加工図（板金／小物／銘版／パネル）",
              display_idx: 12,
            },
            {
              department_id: "D001",
              document_category: 14,
              document_category_text: "部品リスト／材料表",
              display_idx: 13,
            },
            {
              department_id: "D001",
              document_category: 3,
              document_category_text: "取扱説明書",
              display_idx: 14,
            },
            {
              department_id: "D001",
              document_category: 16,
              document_category_text: "検査要領書／検査成績書",
              display_idx: 15,
            },
            {
              department_id: "D001",
              document_category: 17,
              document_category_text: "出荷構成表",
              display_idx: 16,
            },
            {
              department_id: "D001",
              document_category: 8,
              document_category_text: "その他",
              display_idx: 17,
            },
          ],
          document_type: [
            {
              department_id: "D001",
              document_type: 1,
              document_type_text: "WORD",
              display_idx: 1,
            },
            {
              department_id: "D001",
              document_type: 2,
              document_type_text: "EXCEL",
              display_idx: 2,
            },
            {
              department_id: "D001",
              document_type: 3,
              document_type_text: "iCAD",
              display_idx: 3,
            },
            {
              department_id: "D001",
              document_type: 4,
              document_type_text: "電気CAD",
              display_idx: 4,
            },
            {
              department_id: "D001",
              document_type: 5,
              document_type_text: "回路CAD",
              display_idx: 5,
            },
            {
              department_id: "D001",
              document_type: 6,
              document_type_text: "VISIO",
              display_idx: 6,
            },
            {
              department_id: "D001",
              document_type: 7,
              document_type_text: "Power Point",
              display_idx: 7,
            },
            {
              department_id: "D001",
              document_type: 8,
              document_type_text: "その他",
              display_idx: 8,
            },
            {
              department_id: "D001",
              document_type: 9,
              document_type_text: "手書き",
              display_idx: 9,
            },
          ],
          assignee: "グェン ウェン タオ",
          issue_datetime: "2025/11/01",
        },
      };
    }

    if (deptId === "D002") {
      return {
        status: 200,
        respons: {
          document_category: [
            {
              department_id: "D002",
              document_category: 1,
              document_category_text: "組立図",
              display_idx: 1,
            },
            {
              department_id: "D002",
              document_category: 2,
              document_category_text: "製作図",
              display_idx: 2,
            },
            {
              department_id: "D002",
              document_category: 3,
              document_category_text: "フローシート",
              display_idx: 3,
            },
            {
              department_id: "D002",
              document_category: 4,
              document_category_text: "仕様書",
              display_idx: 4,
            },
            {
              department_id: "D002",
              document_category: 5,
              document_category_text: "完成図書",
              display_idx: 5,
            },
            {
              department_id: "D002",
              document_category: 6,
              document_category_text: "計画図",
              display_idx: 6,
            },
            {
              department_id: "D002",
              document_category: 7,
              document_category_text: "図面表",
              display_idx: 7,
            },
            {
              department_id: "D002",
              document_category: 8,
              document_category_text: "購入品リスト",
              display_idx: 8,
            },
            {
              department_id: "D002",
              document_category: 9,
              document_category_text: "検査成績書",
              display_idx: 9,
            },
            {
              department_id: "D002",
              document_category: 10,
              document_category_text: "その他",
              display_idx: 10,
            },
          ],
          document_type: [],
          assignee: "システム 担当者",
          issue_datetime: "2025/11/02",
        },
      };
    }

    if (deptId === "D003") {
      return {
        status: 200,
        respons: {
          document_category: [
            {
              department_id: "D003",
              document_category: 1,
              document_category_text: "仕様書",
              display_idx: 1,
            },
            {
              department_id: "D003",
              document_category: 2,
              document_category_text: "取扱説明書",
              display_idx: 2,
            },
            {
              department_id: "D003",
              document_category: 3,
              document_category_text: "回路図",
              display_idx: 3,
            },
            {
              department_id: "D003",
              document_category: 4,
              document_category_text: "部品配置図（外形寸法図）",
              display_idx: 4,
            },
            {
              department_id: "D003",
              document_category: 5,
              document_category_text: "座標データ、材料表",
              display_idx: 5,
            },
            {
              department_id: "D003",
              document_category: 6,
              document_category_text: "一覧表",
              display_idx: 6,
            },
            {
              department_id: "D003",
              document_category: 7,
              document_category_text: "手順書、指示書",
              display_idx: 7,
            },
            {
              department_id: "D003",
              document_category: 8,
              document_category_text: "加工図、製作図、印刷図",
              display_idx: 8,
            },
            {
              department_id: "D003",
              document_category: 9,
              document_category_text: "銘板図",
              display_idx: 9,
            },
            {
              department_id: "D003",
              document_category: 10,
              document_category_text: "依頼書",
              display_idx: 10,
            },
            {
              department_id: "D003",
              document_category: 11,
              document_category_text: "検査成績書",
              display_idx: 11,
            },
            {
              department_id: "D003",
              document_category: 12,
              document_category_text: "手書き",
              display_idx: 12,
            },
          ],
          document_type: [
            {
              department_id: "D003",
              document_type: 1,
              document_type_text: "ワード",
              display_idx: 1,
            },
            {
              department_id: "D003",
              document_type: 2,
              document_type_text: "エクセル",
              display_idx: 2,
            },
            {
              department_id: "D003",
              document_type: 3,
              document_type_text: "I-CAD",
              display_idx: 3,
            },
            {
              department_id: "D003",
              document_type: 4,
              document_type_text: "回路CAD",
              display_idx: 4,
            },
            {
              department_id: "D003",
              document_type: 5,
              document_type_text: "電気CAD",
              display_idx: 5,
            },
            {
              department_id: "D003",
              document_type: 6,
              document_type_text: "J-STAR",
              display_idx: 6,
            },
            {
              department_id: "D003",
              document_type: 7,
              document_type_text: "一太郎",
              display_idx: 7,
            },
            {
              department_id: "D003",
              document_type: 8,
              document_type_text: "手書き",
              display_idx: 8,
            },
          ],
          assignee: "計測 担当者",
          issue_datetime: "2025/11/03",
        },
      };
    }

    return {
      status: 200,
      respons: {
        document_category: [],
        document_type: [],
        assignee: "",
        issue_datetime: "",
      },
    };
  };

  useEffect(() => {
    const fetchPatternMaster = async () => {
      if (!deptId || !folderId) {
        setPatternMaster({
          document_category: [],
          document_type: [],
          assignee: "",
          issue_datetime: "",
        });
        return;
      }

      setPatternMasterLoading(true);

      try {
        let responseData;
        if (USE_MOCK_API) {
          responseData = getMockPatternMaster(deptId, folderId);
        } else {
          const response = await axiosInstance.post(
            `${BASE_URL}/top/data-new`,
            {
              department_id: deptId,
              folder_id: folderId,
            }
          );
          responseData = response.data;
        }

        const payload = responseData?.respons || {};

        // Debug: Kiểm tra data từ API
        console.log("DocumentRegisterBase - API Response:", responseData);
        console.log("DocumentRegisterBase - Payload:", payload);
        console.log(
          "DocumentRegisterBase - document_category:",
          payload.document_category
        );
        console.log(
          "DocumentRegisterBase - document_type:",
          payload.document_type
        );

        setPatternMaster({
          document_category: sortByDisplayIdx(payload.document_category || []),
          document_type: sortByDisplayIdx(payload.document_type || []),
          assignee: payload.assignee || "",
          issue_datetime: payload.issue_datetime || "",
        });

        // Debug: Kiểm tra state sau khi set
        console.log("DocumentRegisterBase - patternMaster sau khi set:", {
          document_category: sortByDisplayIdx(payload.document_category || []),
          document_type: sortByDisplayIdx(payload.document_type || []),
        });
      } catch (fetchError) {
        console.error("Failed to fetch pattern master", fetchError);
        setPatternMaster({
          document_category: [],
          document_type: [],
          assignee: "",
          issue_datetime: "",
        });
      } finally {
        setPatternMasterLoading(false);
      }
    };

    fetchPatternMaster();
  }, [deptId, folderId]);

  // CommonFormからデータ取得関数
  const handleCommonFormData = useCallback(
    (data) => {
      setCommonFormData(data);
    },
    [setCommonFormData]
  );

  // PatternFormからデータ取得関数
  const handlePatternFormData = useCallback(
    (data) => {
      setPatternFormData(data);
    },
    [setPatternFormData]
  );

  // フォーム送信処理（文書登録）
  const handleSubmit = async (commonData) => {
    try {
      setLoading(true);

      // 必須フィールド検証
      const folderValue =
        (commonData && commonData.folder) || folderNameFromRedux || "";
      if (!folderValue || !folderValue.trim()) {
        setModalMessage("フォルダは必須です。");
        setIsModalOpen(true);
        setLoading(false);
        return false;
      }
      if (!commonData || !commonData.title || !commonData.title.trim()) {
        setModalMessage("タイトルは必須です。");
        setIsModalOpen(true);
        setLoading(false);
        return false;
      }

      // 番号の入力がない時 または オート番号発行していない時
      if (!autoNumber || !autoNumber.trim()) {
        setModalMessage("登録前にオートナンバーを必ず発番してください。");
        setIsModalOpen(true);
        setLoading(false);
        return;
      }

      //APIへ送信する時、 prefixがない：例 "CK-100003" → "100003"
      const rawAutoNumber = autoNumber.includes("-")
        ? autoNumber.split("-").pop()
        : autoNumber;

      const autoNumberValue =
        rawAutoNumber && !Number.isNaN(Number(rawAutoNumber))
          ? Number(rawAutoNumber)
          : rawAutoNumber || "";

      // API送信時、YYYY/MM/DD から YYYY-MM-DDまでコンバート
      const formattedDate = patternFormData.date
        ? patternFormData.date.replace(/\//g, "-")
        : "";

      // API /top/data-registフォーマットによりrequest body
      const baseRequestBody = {
        // auto_number: rawAutoNumber, // prefixがなくて、ナンバーだけ送信する（旧仕様）
        auto_number: autoNumberValue, // 数字として送信（変換できない場合は元の値）
        folder_id: folderId,
        department_id: deptId,
        title: commonData.title || "",
        issue_datetime: formattedDate, // Format YYYY-MM-DD
      };

      // Pattern
      let patternSpecificFields = {};

      // デバッグ用
      console.log(
        "handleSubmit - departmentNameFromRedux:",
        departmentNameFromRedux
      );
      console.log("handleSubmit - departmentId:", deptId);
      console.log("handleSubmit - patternFormData:", patternFormData);

      // PatternKeisou (D001 - 計装・システム制御)
      if (departmentNameFromRedux.includes("計装・システム制御")) {
        patternSpecificFields = {
          product_no: patternFormData.product_no || "", // 製番
          assignee: patternFormData.assignee || "", // 担当者
          product_model: patternFormData.product_model || "", // 型式
          destination: patternFormData.destination || "", // 仕向先
          customer: patternFormData.customer || "", // 得意先
          variety_name: patternFormData.variety_name || "", // 品種名
          document_category: patternFormData.document_category
            ? Number(patternFormData.document_category)
            : null, // 図面種類
          document_type: patternFormData.document_type
            ? Number(patternFormData.document_type)
            : null, // 図面タイプ
          remarks: patternFormData.remarks || "", // 備考
        };
      }
      // Pattern System (D002 - システム装置)
      else if (departmentNameFromRedux.includes("システム装置")) {
        patternSpecificFields = {
          product_no: patternFormData.product_no || "", // 製番
          document_category: patternFormData.document_category
            ? Number(patternFormData.document_category)
            : null, // 図面種類
          destination: patternFormData.destination || "", // 仕向先
          customer: patternFormData.customer || "", // 得意先
          assignee: patternFormData.assignee || "", // 担当者
          device_name: patternFormData.device_name || "", // 設備名
          remarks: patternFormData.remarks || "", // 備考
          assembly_type: patternFormData.assembly_type || "", // 組立種類
        };
      }
      // Pattern Keisoku (D003 - 計測)
      else if (departmentNameFromRedux.includes("計測")) {
        //  必須フィールドチェック
        if (
          !patternFormData.document_category ||
          !patternFormData.document_category.trim()
        ) {
          setModalMessage("図面種類（計測）は必須です。");
          setIsModalOpen(true);
          setLoading(false);
          return false;
        }
        if (
          !patternFormData.category_name ||
          !patternFormData.category_name.trim()
        ) {
          setModalMessage("図面名称は必須です。");
          setIsModalOpen(true);
          setLoading(false);
          return false;
        }
        if (
          !patternFormData.product_model ||
          !patternFormData.product_model.trim()
        ) {
          setModalMessage("製品形式は必須です。");
          setIsModalOpen(true);
          setLoading(false);
          return false;
        }
        if (!patternFormData.date || !patternFormData.date.trim()) {
          setModalMessage("日付は必須です。");
          setIsModalOpen(true);
          setLoading(false);
          return false;
        }
        if (!patternFormData.assignee || !patternFormData.assignee.trim()) {
          setModalMessage("担当者は必須です。");
          setIsModalOpen(true);
          setLoading(false);
          return false;
        }

        patternSpecificFields = {
          document_category: patternFormData.document_category
            ? Number(patternFormData.document_category)
            : null, // 図面種類
          category_name: patternFormData.category_name || "", // 図面名称
          assignee: patternFormData.assignee || "", // 担当者
          product_name: patternFormData.product_name || "", // 製品名
          document_type: patternFormData.document_type
            ? Number(patternFormData.document_type)
            : null, // 図面タイプ
          product_model: patternFormData.product_model || "", // 製品形式
          original_doc_number: patternFormData.original_doc_number || "", // 元図番
          purpose: patternFormData.purpose || "", // 用途
          remarks: patternFormData.remarks || "", // 備考
          customer: patternFormData.customer || "", // 得意先
        };
      }

      // Combine base + pattern-specific fields
      const requestBody = {
        ...baseRequestBody,
        ...patternSpecificFields,
      };

      console.log("Submitting data to /top/data-regist:", requestBody);
      console.log("Base request body:", baseRequestBody);
      console.log("Pattern specific fields:", patternSpecificFields);
      console.log("Common form data:", commonData);
      console.log("Pattern form data:", patternFormData);
      console.log("Auto number:", autoNumberValue);

      let isSuccess = false;

      if (USE_MOCK_API) {
        // ====== MOCK API（現在使用中） ======
        // Mockレスポンス - 登録成功をシミュレート
        await new Promise((resolve) => setTimeout(resolve, 1500)); // 1.5秒遅延
        setModalMessage("登録成功！（Mock）");
        setIsModalOpen(true);
        isSuccess = true;
      } else {
        // ====== 実際のAPI（Reduxを使用） ======
        try {
          const result = await dispatch(registerDocument(requestBody));
          const successMessage =
            result?.message ||
            result?.data?.respons?.message ||
            result?.data?.respons?.msg ||
            "登録成功！";
          setModalMessage(successMessage);
          setIsModalOpen(true);
          isSuccess = Boolean(result?.success);
        } catch (error) {
          // Redux でエラーがディスパッチ済み
          // Redux ステートまたはエラーオブジェクトからエラーメッセージを取得
          const errorMessage =
            reduxError ||
            error?.response?.data?.respons?.message ||
            error?.response?.data?.respons?.msg ||
            error?.response?.data?.message ||
            error?.message ||
            "登録に失敗しました";
          setModalMessage(errorMessage);
          setIsModalOpen(true);
          isSuccess = false;
        }
      }

      // 送信成功後のフォームリセット（成功時のみ）
      if (isSuccess) {
        setAutoNumber("");
        setPatternFormData({});
        setCommonFormData({});
        setIsHakkouDisabled(false); // 発行ボタンを再度有効化
        dispatch(clearFormData()); // Clear Redux form data
        setFormResetToken((prev) => prev + 1);
      }
    } catch (error) {
      // バックエンドからのエラーやネットワークエラー（フォールバック）の処理。
      if (error.response) {
        // バックエンド側でエラーが発生した場合、ステータスコード 500 を返します。
        const message =
          error.response.data?.respons?.message ||
          error.response.data?.respons?.msg ||
          error.response.data?.message ||
          "登録に失敗しました";
        setModalMessage(message);
        setIsModalOpen(true);
      } else {
        // ネットワークエラー
        setModalMessage("ネットワークエラーが発生しました");
        setIsModalOpen(true);
      }
      console.error("Error submitting:", error);
    } finally {
      setLoading(false);
    }
  };

  // 戻る処理
  const handleGoBack = () => {
    // オートナンバーが発行されている場合、確認モーダルを表示
    if (autoNumber && autoNumber.trim()) {
      setConfirmMessage(
        "オートナンバーは発番済みです。\n登録ページを離れてもよろしいですか？"
      );
      setIsConfirmModalOpen(true);
    } else {
      // オートナンバーが発行されていない場合、そのまま戻る
      navigate(-1);
    }
  };

  // 確認モーダルで「はい」を選択した場合の処理
  const handleConfirmGoBack = () => {
    setIsConfirmModalOpen(false);
    navigate(-1);
  };

  // ====== パターン別フォーム描画 ======
  const renderPatternForm = () => {
    // currentUserName は既にコンポーネントの上部でReduxから取得済み

    // デバッグ用
    console.log("DocumentRegisterBase - PatternForm:へProps", {
      documentCategories: patternMaster.document_category,
      documentTypes: patternMaster.document_type,
      optionsLoading: patternMasterLoading,
    });

    const patternProps = {
      onFormDataChange: handlePatternFormData,
      documentCategories: patternMaster.document_category,
      documentTypes: patternMaster.document_type,
      currentUserName: currentUserName,
      optionsLoading: patternMasterLoading,
      resetToken: formResetToken,
    };

    // truyền props xuống các component con
    if (departmentNameFromRedux.includes("計装・システム制御")) {
      return <PatternKeisouForm {...patternProps} />;
    }

    if (departmentNameFromRedux.includes("システム装置")) {
      return <PatternSystemForm {...patternProps} />;
    }

    if (departmentNameFromRedux.includes("計測")) {
      return <PatternKeisokuForm {...patternProps} />;
    }

    return (
      <div className="pattern-placeholder">
        属性パターンを選択してください。
      </div>
    );
  };

  return (
    <div className="contents">
      <Breadcrumb
        userName={showUserName}
        departmentName={departmentNameFromRedux}
        folderName={folderNameFromRedux}
        deptId={deptId}
        folderId={folderId}
      />
      <div className="document-register-container">
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          message={modalMessage}
        />
        {/* 確認モーダル（戻るボタン用） */}
        <Modal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmGoBack}
          message={confirmMessage}
          showCancelButton={true}
        />
        <h2 className="page-title">文書登録</h2>

        {/* Common Form */}
        <CommonForm
          currentPattern={inferredPattern}
          setCurrentPattern={handlePatternChange}
          onSubmit={handleSubmit}
          onGoBack={handleGoBack}
          onIssue={handleIssue}
          autoNumber={autoNumber}
          attributePatternText={getAttributePatternText()}
          onFormDataChange={handleCommonFormData}
          loading={loading}
          resetToken={formResetToken}
          isHakkouDisabled={isHakkouDisabled}
        />

        {/* Pattern Form */}
        <div className="pattern-area">{renderPatternForm()}</div>

        {/*ボタングループ */}
        <div className="button-group">
          <button
            type="button"
            className="btn back-btn"
            onClick={handleGoBack}
            disabled={loading}
          >
            戻る
          </button>
          <button
            type="button"
            className="btn register-btn"
            onClick={() => handleSubmit(commonFormData)}
            disabled={loading}
          >
            登録
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentRegisterBase;
